/**
 * D2 — HISTÓRICO DA CHECAGEM DO CONVITE: as últimas 30 checagens numa faixa + a frase pronta.
 *
 * "Funcionou em 96% das checagens · último erro ontem às 14h: o WhatsApp não respondeu."
 *
 * A taxa vem do servidor (invariante 3), e lá **checagem sem resposta conta como falha**.
 * Sem checagem nenhuma é "—" com o motivo, nunca "100%" nem "0%". Carregando é skeleton no
 * formato final. Estado em dois canais: quadradinho colorido + a lista "Ver as checagens".
 *
 * A faixa é UMA imagem com nome (`role="img"` + `aria-label`): os quadradinhos não recebem
 * foco — 10px não dão alvo de toque, e um foco sem nome não ajuda o leitor de tela. O detalhe
 * de cada checagem (hora e motivo da falha) mora na lista aberta por um `<details>`, que
 * funciona com teclado, leitor de tela e toque.
 */
import { NO_DATA_DASH, Skeleton, cn, formatPercent } from '@mgdlead/ui';

import { useGroupLinkChecks } from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';

export interface LinkCheckHistoryProps {
  workspaceId: string;
  groupId: string;
  timezone: string;
}

export function LinkCheckHistory({ workspaceId, groupId, timezone }: LinkCheckHistoryProps) {
  const query = useGroupLinkChecks(workspaceId, groupId);

  if (query.isPending) {
    return <Skeleton className="h-8 w-full" />;
  }
  if (query.isError) {
    return (
      <p className="text-xs text-muted-foreground">
        Não conseguimos carregar o histórico das checagens.
      </p>
    );
  }

  const hist = query.data;
  if (!hist) return null;

  if (hist.total === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        <span aria-hidden="true" className="mr-1">
          {NO_DATA_DASH}
        </span>
        O convite ainda não foi conferido. A primeira checagem acontece quando o grupo entra numa
        campanha ou quando você busca o convite.
      </p>
    );
  }

  const quandoErro =
    hist.lastError === null
      ? null
      : (formatRelative(hist.lastError.checkedAt) ??
        formatExact(hist.lastError.checkedAt, timezone));

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="flex flex-wrap items-center gap-0.75"
        role="img"
        aria-label={`Últimas ${hist.total} checagens do convite: ${hist.succeeded} funcionaram`}
      >
        {hist.checks.map((check) => (
          <span
            key={check.checkedAt}
            aria-hidden="true"
            className={cn('size-2.5 rounded-xs', check.ok ? 'bg-success' : 'bg-danger')}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Funcionou em{' '}
        <span className="nums font-medium text-foreground">
          {hist.successRate === null ? NO_DATA_DASH : formatPercent(hist.successRate, 0)}
        </span>{' '}
        das {hist.total} últimas checagens
        {hist.lastError !== null ? (
          <span title={formatExact(hist.lastError.checkedAt, timezone) ?? undefined}>
            {' '}
            · último erro {quandoErro}: {hist.lastError.cause}
          </span>
        ) : (
          ' · nenhum erro'
        )}
      </p>
      <details className="text-xs text-muted-foreground">
        <summary className="inline-flex min-h-11 cursor-pointer items-center rounded-xs hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-0">
          Ver as checagens
        </summary>
        <ul className="mt-1 flex flex-col gap-0.5">
          {hist.checks.map((check) => (
            <li key={check.checkedAt} className="nums">
              {formatExact(check.checkedAt, timezone) ?? check.checkedAt} —{' '}
              {check.ok ? 'funcionou' : (check.cause ?? 'falhou')}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
